import { useEffect, useRef } from 'react'

// Esri JS API is AMD; we load via window.require from the script tag in index.html
declare global { interface Window { require:any } }

type Coord = [number, number] // [lat, lng]

export default function ArcGISPolygonEditor({
  coords, onChange
}:{ coords: Coord[]; onChange:(c:Coord[])=>void }) {
  const node = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let view:any, map:any, sketch:any, graphicsLayer:any

    window.require([
      'esri/Map',
      'esri/views/MapView',
      'esri/layers/GraphicsLayer',
      'esri/Graphic',
      'esri/widgets/Sketch'
    ], (Map:any, MapView:any, GraphicsLayer:any, Graphic:any, Sketch:any) => {

      graphicsLayer = new GraphicsLayer()
      map = new Map({ basemap:'topo-vector', layers:[graphicsLayer] })

      view = new MapView({
        container: node.current!,
        map,
        center: [coords[0]?.[1] ?? -80, coords[0]?.[0] ?? 33], // [lng, lat]
        zoom: coords.length? 14 : 5
      })

      const poly = coords.length ? new Graphic({
        geometry: { type:'polygon', rings: [ coords.map(c => [c[1], c[0]]) ] },
        symbol: { type:'simple-fill', color:[78,143,116,0.2], outline:{ color:[78,143,116], width:2 } }
      }) : null

      if (poly) graphicsLayer.add(poly)

      sketch = new Sketch({
        view, layer: graphicsLayer,
        creationMode:'update',
        visibleElements: { selectionTools:{ 'rectangle-selection':false, 'lasso-selection':false } }
      })
      view.ui.add(sketch, 'top-right')

      // When polygon edited/created, push coords back out as [lat,lng]
      sketch.on('create', (e:any) => {
        if (e.state === 'complete') emit(e.graphic)
      })
      sketch.on('update', (e:any) => {
        if (e.state === 'complete') emit(e.graphics[0])
      })

      function emit(graphic:any){
        const rings: number[][] = graphic.geometry.rings?.[0] ?? []
        const latlng: Coord[] = rings.map(([lng,lat]) => [lat,lng])
        onChange(latlng)
      }
    })

    return () => {
      try { view?.destroy?.() } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <div ref={node} className="h-80 rounded-xl border border-slate-200" />
}
